/* ServiceNow and Jira: the provider catalogue, the connections, and this tenant's ticket rules.
 *
 * **No PUT on a ticket rule, because the API has none.** Migration 0113 grants no `UPDATE`, so a
 * rule is declared and withdrawn rather than edited — the audit chain then carries both halves of a
 * change, and "when did our incidents start appearing in that project" is answerable from the chain
 * rather than inferable from a row's current value. A UI offering an edit would be offering
 * something the server refuses. `alert-routing` makes the same call against the same grant shape.
 *
 * **Every read is invalidated after a mutation rather than patched optimistically.** A registration
 * can be refused server-side — an instance URL outside the deployment's allow-list is a 422 — and a
 * verification's whole point is that its answer comes from the provider. An optimistic patch would
 * show a connection in a state nobody has confirmed, on the screens whose subject is exactly
 * whether these systems are reachable.
 *
 * **The credential never comes back.** `ItsmCredentials` is a request body with no response that
 * carries it: `useReplaceCredential` returns the `ItsmConnection`, which has no field material would
 * fit in. So nothing here puts a password into a query cache, where it would outlive the form.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  isIncidentTicketList,
  isIncidentTicketStatus,
  isItsmConnection,
  isItsmConnectionList,
  isProviderList,
  isTicketPolicy,
  isTicketRule,
  isVerificationResult,
} from '@/shared/api/generated/itsm';
import type {
  IncidentTicketSummary,
  IncidentTicketStatus,
  ItsmConnection,
  ItsmConnectionCreate,
  ItsmConnectionList,
  ItsmCredentials,
  ProviderList,
  TicketPolicy,
  TicketRule,
  TicketRuleCreate,
  VerificationResult,
} from '@/shared/api/generated/itsm';
import {
  forgetIdempotencyKey,
  getOne,
  idempotencyKeyFor,
  post,
  put,
  remove,
} from '@/shared/api/http';

export const ITSM_URL = '/v1/itsm';

const PROVIDERS_KEY = ['itsm', 'providers'] as const;
const CONNECTIONS_KEY = ['itsm', 'connections'] as const;
const RULES_KEY = ['itsm', 'ticket-rules'] as const;

/** What this build can connect to, available or not.
 *
 * **`staleTime: Infinity`, which is the one read here that deserves it.** The answer is a property
 * of the deployment's wiring rather than of anything a tenant can change, so it cannot move while
 * the tab is open — and refetching it on every mount of the connect dialog would be a request whose
 * answer is known to be identical.
 */
export function useProviders(): UseQueryResult<ProviderList> {
  return useQuery({
    queryKey: PROVIDERS_KEY,
    queryFn: async () =>
      getOne(`${ITSM_URL}/providers`, isProviderList, 'provider catalogue'),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

export function useConnections(): UseQueryResult<ItsmConnectionList> {
  return useQuery({
    queryKey: CONNECTIONS_KEY,
    queryFn: async () =>
      getOne(`${ITSM_URL}/connections`, isItsmConnectionList, 'ITSM connections'),
    retry: false,
  });
}

export function useRegisterConnection(): UseMutationResult<
  ItsmConnection,
  Error,
  ItsmConnectionCreate
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (write: ItsmConnectionCreate) =>
      post(
        `${ITSM_URL}/connections`,
        /* **Keyed on the identity, never on the whole body.** Every other creating POST in this app
         * keys on `JSON.stringify(write)`, and doing that here would put a password into the key —
         * and the key into `sessionStorage`, where it would outlive the form that collected it. The
         * provider and the instance URL are what make two registrations the same registration, so a
         * retry of one carries the same key while a second service desk gets its own. */
        idempotencyKeyFor('itsm-connection', `${write.provider}:${write.instance_url}`),
        isItsmConnection,
        'ITSM connection',
        write,
      ),
    onSuccess: (_result, write) => {
      forgetIdempotencyKey('itsm-connection', `${write.provider}:${write.instance_url}`);
      void client.invalidateQueries({ queryKey: CONNECTIONS_KEY });
    },
  });
}

/** Ask the provider whether the stored credential still works.
 *
 * **The key carries the moment, which is the opposite of what a key usually does.** A verification
 * has no effect to replay: it reads, it records an answer about the instant it ran, and running it
 * twice is two honest readings rather than one duplicated one. Suppressing the second would mean a
 * credential rotated between two clicks still reported the verdict from before the rotation — on
 * the one control whose entire purpose is to produce a fresh reading.
 */
export function useVerifyConnection(): UseMutationResult<
  VerificationResult,
  Error,
  string
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (connectionId: string) =>
      post(
        `${ITSM_URL}/connections/${encodeURIComponent(connectionId)}/verify`,
        idempotencyKeyFor('itsm-verify', `${connectionId}:${String(Date.now())}`),
        isVerificationResult,
        'verification result',
        {},
      ),
    /* `onSettled`, not `onSuccess`: a verification that *failed* has still moved the connection's
     * state and its `last_checked_at`, so the list is stale either way. Refetching only on success
     * is what would leave the screen reporting `verified` for a connection the server has just
     * recorded as unreachable — which is the one reading this control exists to produce. */
    onSettled: () => {
      void client.invalidateQueries({ queryKey: CONNECTIONS_KEY });
    },
  });
}

export interface CredentialReplacement {
  readonly connectionId: string;
  readonly credentials: ItsmCredentials;
}

export function useReplaceCredential(): UseMutationResult<
  ItsmConnection,
  Error,
  CredentialReplacement
> {
  const client = useQueryClient();
  return useMutation({
    /* A PUT, so no idempotency key: the subject is named in the URL and the body is the desired
     * state rather than a delta, which makes a retry the same request by construction. */
    mutationFn: async (change: CredentialReplacement) =>
      put(
        `${ITSM_URL}/connections/${encodeURIComponent(change.connectionId)}/credential`,
        isItsmConnection,
        'ITSM connection',
        change.credentials,
      ),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: CONNECTIONS_KEY });
    },
  });
}

export interface ConnectionEnabledChange {
  readonly connectionId: string;
  readonly enabled: boolean;
}

/** Switch a connection off without losing it, or bring it back.
 *
 * **Disabling is not deleting**, which is why both controls exist. Deleting loses the instance URL,
 * the project and the credential, so restoring it sends an operator back to their service desk for
 * a new token — and every ticket rule pointing at it goes with it. A maintenance window is the
 * ordinary case, and `DISABLED` is the one state the queue reads as "do not use".
 */
export function useSetConnectionEnabled(): UseMutationResult<
  ItsmConnection,
  Error,
  ConnectionEnabledChange
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (change: ConnectionEnabledChange) =>
      put(
        `${ITSM_URL}/connections/${encodeURIComponent(change.connectionId)}/enabled`,
        isItsmConnection,
        'ITSM connection',
        { enabled: change.enabled },
      ),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: CONNECTIONS_KEY });
    },
  });
}

export function useDeleteConnection(): UseMutationResult<void, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (connectionId: string) =>
      remove(`${ITSM_URL}/connections/${encodeURIComponent(connectionId)}`),
    /* Both lists, because deleting a connection withdraws every rule that pointed at it. Refetching
     * only the connections would leave the rules table naming a destination that no longer exists —
     * worse than a stale row, because it reads as a rule that is still filing tickets. */
    onSettled: () => {
      void client.invalidateQueries({ queryKey: CONNECTIONS_KEY });
      void client.invalidateQueries({ queryKey: RULES_KEY });
    },
  });
}

export function useTicketPolicy(): UseQueryResult<TicketPolicy> {
  return useQuery({
    queryKey: RULES_KEY,
    queryFn: async () =>
      getOne(`${ITSM_URL}/ticket-rules`, isTicketPolicy, 'ticket policy'),
    retry: false,
  });
}

export function useDeclareTicketRule(): UseMutationResult<
  TicketRule,
  Error,
  TicketRuleCreate
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (write: TicketRuleCreate) =>
      post(
        `${ITSM_URL}/ticket-rules`,
        /* Keyed on the rule's own content, so a retried submission of the same rule carries the same
         * key and a genuinely different rule gets a new one. There is no credential in this body. */
        idempotencyKeyFor('itsm-ticket-rule', JSON.stringify(write)),
        isTicketRule,
        'ticket rule',
        write,
      ),
    onSuccess: (_result, write) => {
      forgetIdempotencyKey('itsm-ticket-rule', JSON.stringify(write));
      void client.invalidateQueries({ queryKey: RULES_KEY });
    },
  });
}

export function useWithdrawTicketRule(): UseMutationResult<void, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (ruleId: string) =>
      remove(`${ITSM_URL}/ticket-rules/${encodeURIComponent(ruleId)}`),
    /* `onSettled`: a 404 means somebody else withdrew it, and the list is stale either way. */
    onSettled: () => {
      void client.invalidateQueries({ queryKey: RULES_KEY });
    },
  });
}

/** Whether one incident produced a ticket, and where it went.
 *
 * Read by the incident detail screen. **`attempted: false` is not an error and not an absence** —
 * it is the tenant's own policy saying no ticket is filed for an incident of this shape, which is
 * the distinction `NotificationOutcome.attempted` draws for an alert that was deliberately not
 * sent. A failed read is a third thing again, and the panel that renders this keeps all three apart.
 */
/** The newest ticket attempt for a page of incidents, in one request.
 *
 * **One read rather than one per row**, which is the N+1 the incident queue would otherwise be:
 * it renders up to fifty rows, and `/v1/itsm` has no `PLATFORM_ROUTES` entry, so it falls to the
 * edge's catch-all bucket at ten requests a minute — a single page load would rate-limit itself.
 *
 * **An id that is absent from the answer raised no ticket.** The route answers for exactly the ids
 * it was given and refuses more than it will read, so absence is a fact rather than a gap, and a
 * caller may render it as "no rule matched" without a second request.
 *
 * Keyed on the sorted ids, so two screens showing the same page share the cache and a re-render
 * that reorders the rows does not refetch. Disabled on an empty page rather than asking about
 * nothing.
 */
export function useIncidentTickets(
  incidentIds: readonly string[],
): UseQueryResult<ReadonlyMap<string, IncidentTicketSummary>> {
  const ids = [...incidentIds].sort();
  return useQuery({
    queryKey: ['itsm', 'incident-tickets', ids] as const,
    enabled: ids.length > 0,
    queryFn: async () => {
      const query = ids.map((id) => `incident_id=${encodeURIComponent(id)}`).join('&');
      const page = await getOne(
        `${ITSM_URL}/incident-tickets?${query}`,
        isIncidentTicketList,
        'incident ticket list',
      );
      return new Map(page.items.map((item) => [item.incident_id, item]));
    },
    retry: false,
  });
}

export function useIncidentTicket(
  incidentId: string,
): UseQueryResult<IncidentTicketStatus> {
  return useQuery({
    queryKey: ['itsm', 'incident-ticket', incidentId] as const,
    queryFn: async () =>
      getOne(
        `${ITSM_URL}/incidents/${encodeURIComponent(incidentId)}/ticket`,
        isIncidentTicketStatus,
        'incident ticket status',
      ),
    retry: false,
  });
}
